import { mapFieldControlType } from '../../components/PatientForm/PatientFormFields';
import type {
  FormFillSession,
  PatientFormBlockPayloadValue,
  PatientFormSubmissionPayload,
  PatientFormValues,
  PublicFormSignatureUploadResponse,
  ResolvedForm,
  ResolvedFormBlock,
} from './types';
import type { PatientFormMediaContextValue } from '../../components/PatientForm/PatientFormMediaContext';

/** Location of the signature block/field — uploaded separately as `signature_file_key`. */
export interface SignatureLocation {
  blockId: string;
  fieldId?: string;
}

/** Every visible signature pad or nested signature field on the form. */
export function findAllSignatureLocations(form: ResolvedForm): SignatureLocation[] {
  const out: SignatureLocation[] = [];
  for (const block of form.blocks) {
    if (!block.is_visible) continue;
    if (mapFieldControlType(block.block_type) === 'signature') {
      out.push({ blockId: block.id });
      continue;
    }
    for (const field of block.catalog?.fields ?? []) {
      if (mapFieldControlType(field.type ?? 'text') === 'signature') {
        out.push({ blockId: block.id, fieldId: field.id });
      }
    }
  }
  return out;
}

/** Alias used by digital-forms callers (W8/D27). */
export const findSignatureLocations = findAllSignatureLocations;

export function findSignatureLocation(form: ResolvedForm): SignatureLocation | undefined {
  return findAllSignatureLocations(form)[0];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isPendingFileValue(value: unknown): value is File {
  return typeof File !== 'undefined' && value instanceof File;
}

const SIGNATURE_OBJECT_KEYS = [
  'url',
  'signature_url',
  'signed_url',
  'file_key',
  'signature_file_key',
  'key',
  'pad',
  'signature',
] as const;

function coerceSignatureString(raw: unknown): string | undefined {
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (!isRecord(raw)) return undefined;
  for (const key of SIGNATURE_OBJECT_KEYS) {
    const nested = raw[key];
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
  }
  return undefined;
}

function isHttpOrDataSrc(value: string): boolean {
  return (
    value.startsWith('data:') ||
    value.startsWith('blob:') ||
    value.startsWith('http://') ||
    value.startsWith('https://')
  );
}

export function isRenderableAssetSrc(value: string | undefined): boolean {
  return Boolean(value && (isHttpOrDataSrc(value) || value.startsWith('/')));
}

function asMediaFile(entry: unknown): { file_key?: string; url?: string } | undefined {
  if (typeof entry === 'string' && entry.trim()) {
    return isHttpOrDataSrc(entry) ? { url: entry } : { file_key: entry };
  }
  if (!isRecord(entry)) return undefined;
  const file_key =
    (typeof entry.file_key === 'string' && entry.file_key) ||
    (typeof entry.key === 'string' && entry.key) ||
    (typeof entry.signature_file_key === 'string' && entry.signature_file_key) ||
    undefined;
  const url =
    (typeof entry.url === 'string' && entry.url) ||
    (typeof entry.signed_url === 'string' && entry.signed_url) ||
    (typeof entry.signature_url === 'string' && entry.signature_url) ||
    undefined;
  if (!file_key && !url) return undefined;
  return { ...(file_key ? { file_key } : {}), ...(url ? { url } : {}) };
}

function rememberUrl(map: Record<string, string>, key: string | undefined, url: string | undefined) {
  if (key && url) map[key] = url;
}

/** Turn a stored upload key into a fetchable URL for read-only previews. */
export interface FormMediaContext extends PatientFormMediaContextValue {}

export function buildMediaContextFromSession(session: FormFillSession): PatientFormMediaContextValue {
  const submission = session.submission;
  const fileUrlsByKey: Record<string, string> = {};
  const fileUrlsByBlockId: Record<string, string> = {};
  const signatureUrlsByBlockId: Record<string, string> = {};
  const signatureKeys = submission.signature_keys ?? {};
  const locations = findAllSignatureLocations(session.resolved_form);

  for (const [key, rawEntry] of Object.entries(submission.media?.files ?? {})) {
    const entry = asMediaFile(rawEntry);
    if (!entry) continue;
    rememberUrl(fileUrlsByKey, entry.file_key, entry.url);
    rememberUrl(fileUrlsByKey, key, entry.url);
    if (!entry.url) continue;
    fileUrlsByBlockId[key] = entry.url;
    const dot = key.indexOf('.');
    if (dot > 0) {
      signatureUrlsByBlockId[key.slice(0, dot)] = entry.url;
    } else {
      signatureUrlsByBlockId[key] = entry.url;
    }
  }

  for (const block of session.resolved_form.blocks) {
    if (block.value_url) fileUrlsByBlockId[block.id] = block.value_url;
    if (block.signature_url) signatureUrlsByBlockId[block.id] = block.signature_url;
  }

  for (const loc of locations) {
    const mediaKey = signatureMediaFieldKey(loc);
    const payloadValue = readSignatureValue(submission.payload, loc);
    const storedKey =
      (payloadValue && isStoredMediaKey(payloadValue) ? payloadValue : undefined) ??
      signatureKeys[mediaKey] ??
      signatureKeys[loc.blockId];
    if (storedKey && fileUrlsByKey[storedKey]) {
      signatureUrlsByBlockId[loc.blockId] = fileUrlsByKey[storedKey];
      fileUrlsByBlockId[mediaKey] = fileUrlsByKey[storedKey];
      continue;
    }
    const url = fileUrlsByBlockId[mediaKey] ?? signatureUrlsByBlockId[loc.blockId];
    if (url) signatureUrlsByBlockId[loc.blockId] = url;
  }

  const loneLocation = locations.length === 1 ? locations[0] : undefined;
  if (loneLocation && !signatureUrlsByBlockId[loneLocation.blockId]) {
    const uniqueFileUrls = [...new Set(Object.values(fileUrlsByBlockId))];
    if (uniqueFileUrls.length === 1 && uniqueFileUrls[0]) {
      signatureUrlsByBlockId[loneLocation.blockId] = uniqueFileUrls[0];
    }
  }

  const topSignatureUrl = submission.signature_url ?? submission.media?.signature_url ?? undefined;
  if (topSignatureUrl) {
    const firstMissing = locations.find((loc) => !signatureUrlsByBlockId[loc.blockId]);
    const noneMapped = locations.every((loc) => !signatureUrlsByBlockId[loc.blockId]);
    if (loneLocation && !signatureUrlsByBlockId[loneLocation.blockId]) {
      signatureUrlsByBlockId[loneLocation.blockId] = topSignatureUrl;
    } else if (noneMapped && firstMissing) {
      signatureUrlsByBlockId[firstMissing.blockId] = topSignatureUrl;
    }
  }

  return {
    ...(submission.id ?? submission.submission_id
      ? { submissionId: submission.id ?? submission.submission_id }
      : {}),
    ...(session.assignment.id ? { assignmentId: session.assignment.id } : {}),
    fileUrlsByKey,
    fileUrlsByBlockId,
    signatureUrlsByBlockId,
  };
}

export function isStoredMediaKey(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (isHttpOrDataSrc(v) || v.startsWith('/')) return false;
  if (v.includes('/') || v.includes('\\')) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) return true;
  return /^[A-Za-z0-9._-]{8,}$/.test(v);
}

export function resolveFormAssetUrl(
  fileKey: string,
  context?: FormMediaContext,
  blockId?: string,
  fieldId?: string,
): string {
  if (fileKey && isHttpOrDataSrc(fileKey)) return fileKey;

  if (blockId) {
    const mediaKey = fieldId ? `${blockId}.${fieldId}` : blockId;
    if (context?.fileUrlsByBlockId?.[mediaKey]) return context.fileUrlsByBlockId[mediaKey];
    if (context?.signatureUrlsByBlockId?.[blockId]) return context.signatureUrlsByBlockId[blockId];
    if (context?.fileUrlsByBlockId?.[blockId]) return context.fileUrlsByBlockId[blockId];
  }
  if (fileKey && context?.fileUrlsByKey?.[fileKey]) return context.fileUrlsByKey[fileKey];
  if (!fileKey) return '';

  const key = encodeURIComponent(fileKey.replace(/^\/+/, ''));
  // Same path the patient-fill app uses — token in the URL so <img> can load it.
  if (context?.accessToken) {
    return `/api/public/forms/${encodeURIComponent(context.accessToken)}/file?key=${key}`;
  }
  if (context?.submissionId) {
    return `/api/forms/submissions/${encodeURIComponent(context.submissionId)}/file?key=${key}`;
  }
  if (context?.assignmentId) {
    return `/api/forms/assignments/${encodeURIComponent(context.assignmentId)}/file?key=${key}`;
  }
  return `/api/forms/file?key=${key}`;
}

function isBrowserSafeAssetSrc(src: string): boolean {
  if (isHttpOrDataSrc(src) || src.startsWith('blob:')) return true;
  // Public fill puts the access token in the path, so <img> can load it.
  return src.startsWith('/api/public/forms/');
}

/** Resolve a signature field value + media context into an <img>/canvas src. */
export function resolveSignatureDisplaySrc(
  value: unknown,
  context?: FormMediaContext,
  blockId?: string,
  fieldId?: string,
): string {
  const raw = coerceSignatureString(value) ?? '';
  if (isHttpOrDataSrc(raw)) return raw;
  const resolved = resolveFormAssetUrl(raw, context, blockId, fieldId);
  return isBrowserSafeAssetSrc(resolved) ? resolved : '';
}

export interface SignatureFileTarget {
  loc: SignatureLocation;
  fileKey: string;
  mediaKey: string;
}

/** Storage keys that still need an authenticated fetch (no signed URL on the wire). */
export function collectSignatureFileTargets(session: FormFillSession): SignatureFileTarget[] {
  const out: SignatureFileTarget[] = [];
  const seen = new Set<string>();
  const keys = session.submission.signature_keys ?? {};
  const locations = findAllSignatureLocations(session.resolved_form);

  for (const loc of locations) {
    const mediaKey = signatureMediaFieldKey(loc);
    const block = session.resolved_form.blocks.find((item) => item.id === loc.blockId);
    const fileKey = pickStoredSignatureKey(
      readSignatureValue(session.submission.payload, loc),
      keys[mediaKey],
      keys[loc.blockId],
      block?.signature_file_key,
      block?.file_key,
      session.submission.signature_file_key ?? undefined,
    );
    if (!fileKey) continue;
    const dedupe = `${mediaKey}:${fileKey}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    out.push({ loc, fileKey, mediaKey });
  }

  return out;
}

export function extractUploadFileKey(
  res: PublicFormSignatureUploadResponse,
): string | undefined {
  return res.file_key ?? res.signature_file_key ?? res.key;
}

export function signatureMediaFieldKey(loc: SignatureLocation): string {
  return loc.fieldId ? `${loc.blockId}.${loc.fieldId}` : loc.blockId;
}

function writeSignatureValue(
  values: PatientFormValues,
  loc: SignatureLocation,
  next: string,
): PatientFormValues {
  if (loc.fieldId) {
    const block =
      isRecord(values[loc.blockId]) && !isPendingFileValue(values[loc.blockId])
        ? { ...(values[loc.blockId] as Record<string, unknown>) }
        : {};
    block[loc.fieldId] = next;
    return { ...values, [loc.blockId]: block as PatientFormBlockPayloadValue };
  }
  return { ...values, [loc.blockId]: next };
}

function isDisplayableSignatureValue(value: string | undefined): boolean {
  return Boolean(
    value &&
      (value.startsWith('data:') || value.startsWith('http://') || value.startsWith('https://') || isStoredMediaKey(value)),
  );
}

export function readSignatureValue(
  values: PatientFormValues,
  loc: SignatureLocation,
): string | undefined {
  const blockVal = values[loc.blockId];
  if (loc.fieldId && isRecord(blockVal)) {
    const fromField = coerceSignatureString(blockVal[loc.fieldId]);
    if (fromField) return fromField;
    if (loc.fieldId === 'pad') return coerceSignatureString(blockVal.signature);
    if (loc.fieldId === 'signature') return coerceSignatureString(blockVal.pad);
    return undefined;
  }
  return coerceSignatureString(blockVal);
}

function pickStoredSignatureKey(...candidates: Array<string | undefined>): string | undefined {
  for (const candidate of candidates) {
    if (candidate && (candidate.startsWith('data:') || isStoredMediaKey(candidate))) return candidate;
  }
  return undefined;
}

/** Restore every signature field from payload keys and submission media metadata. */
export function hydrateSignatureValues(
  form: ResolvedForm,
  values: PatientFormValues,
  session: FormFillSession,
): PatientFormValues {
  let next = values;
  const payload = session.submission.payload;
  const mediaFiles = session.submission.media?.files ?? {};
  const signatureKeys = session.submission.signature_keys ?? {};
  const topSignatureKey = session.submission.signature_file_key;
  const locations = findAllSignatureLocations(form);

  for (const loc of locations) {
    const current = readSignatureValue(next, loc);
    if (isDisplayableSignatureValue(current)) continue;

    const fromPayload = readSignatureValue(payload, loc);
    const mediaKey = signatureMediaFieldKey(loc);
    const mediaEntry =
      asMediaFile(mediaFiles[mediaKey]) ??
      asMediaFile(mediaFiles[loc.blockId]) ??
      asMediaFile(mediaFiles[`${loc.blockId}.pad`]) ??
      asMediaFile(mediaFiles[`${loc.blockId}.signature`]);
    const block = form.blocks.find((item) => item.id === loc.blockId);
    const first = locations[0];
    const isFirst = Boolean(
      first && loc.blockId === first.blockId && loc.fieldId === first.fieldId,
    );

    const stored = pickStoredSignatureKey(
      fromPayload,
      mediaEntry?.file_key,
      signatureKeys[mediaKey],
      signatureKeys[loc.blockId],
      block?.signature_file_key,
      block?.file_key,
      isFirst ? (topSignatureKey ?? undefined) : undefined,
    );
    if (stored) next = writeSignatureValue(next, loc, stored);
  }

  return next;
}

/**
 * A basic block bound to a composite patient field (`patient.address`) may get its prefill as
 * parts; the block holds one text answer, so join them into the complete value.
 */
function basicBlockPrefill(prefill: unknown): PatientFormBlockPayloadValue {
  if (!isRecord(prefill)) return prefill as PatientFormBlockPayloadValue;
  return Object.values(prefill)
    .filter((part): part is string | number => typeof part === 'string' || typeof part === 'number')
    .map((part) => String(part).trim())
    .filter(Boolean)
    .join(', ');
}

/** Seed in-form values from submission payload (+ optional patient name defaults). */
export function buildInitialValues(session: FormFillSession): PatientFormValues {
  const values: PatientFormValues = { ...session.submission.payload };

  const patientDefaults: Record<string, unknown> = {};
  if (session.patient.first_name) patientDefaults.first_name = session.patient.first_name;
  if (session.patient.last_name) patientDefaults.last_name = session.patient.last_name;

  if (Object.keys(patientDefaults).length > 0) {
    for (const block of session.resolved_form.blocks) {
      const fields = block.catalog?.fields;
      if (!fields || fields.length === 0) continue;
      const fieldIds = new Set(fields.map((f) => f.id));
      const applicable = Object.fromEntries(
        Object.entries(patientDefaults).filter(([id]) => fieldIds.has(id)),
      );
      if (Object.keys(applicable).length === 0) continue;

      const draft =
        values[block.id] && typeof values[block.id] === 'object' && !Array.isArray(values[block.id])
          ? (values[block.id] as Record<string, unknown>)
          : {};
      values[block.id] = { ...applicable, ...draft } as PatientFormBlockPayloadValue;
    }
  }

  for (const block of session.resolved_form.blocks) {
    if (!block.is_visible) continue;
    const fields = block.catalog?.fields;
    if (fields && fields.length > 0) {
      const existing = isRecord(values[block.id]) ? (values[block.id] as Record<string, unknown>) : {};
      let record = existing;
      for (const field of fields) {
        if (field.prefill === undefined) continue;
        if (!isEmptyValue(record[field.id])) continue;
        if (record === existing) record = { ...existing };
        record[field.id] = field.prefill;
      }
      if (record !== existing) values[block.id] = record as PatientFormBlockPayloadValue;
    } else if (block.prefill !== undefined && isEmptyValue(values[block.id])) {
      values[block.id] = basicBlockPrefill(block.prefill);
    }
  }

  return hydrateSignatureValues(session.resolved_form, values, session);
}

/** @deprecated Prefer `hydrateSignatureValues` — kept for existing imports. */
export function applySignatureFileKeyToValues(
  form: ResolvedForm,
  values: PatientFormValues,
  signatureFileKey: string | null | undefined,
  signatureUrl?: string | null,
): PatientFormValues {
  const sessionLike = {
    resolved_form: form,
    submission: {
      payload: values,
      signature_file_key: signatureFileKey,
      signature_url: signatureUrl,
      media: signatureUrl ? { signature_url: signatureUrl } : undefined,
    },
  } as FormFillSession;
  return hydrateSignatureValues(form, values, sessionLike);
}

export function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (isPendingFileValue(value)) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isRecord(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) return true;
    return entries.every(([, nested]) => isEmptyValue(nested));
  }
  return false;
}

/** A required checkbox the patient ticked and then unticked holds `false` — still unanswered. */
function isMissingRequired(value: unknown): boolean {
  return value === false || isEmptyValue(value);
}

export function findMissingRequiredBlocks(
  form: ResolvedForm,
  values: PatientFormValues,
): string[] {
  const missing: string[] = [];
  for (const block of form.blocks) {
    if (!block.is_visible) continue;
    const fields = block.catalog?.fields;
    if (fields && fields.length > 0) {
      const record = isRecord(values[block.id]) ? (values[block.id] as Record<string, unknown>) : {};
      const hasMissingField = fields.some(
        (field) => (field.required ?? false) && isMissingRequired(record[field.id]),
      );
      if (hasMissingField) missing.push(block.id);
    } else if (block.is_required && isMissingRequired(values[block.id])) {
      missing.push(block.id);
    }
  }
  return missing;
}

/** Strip in-memory `File` picks until upload replaces them with stored keys. */
function sanitizeWireValue(value: unknown): unknown {
  if (isPendingFileValue(value)) return undefined;
  if (isRecord(value)) {
    const next = Object.fromEntries(
      Object.entries(value)
        .map(([key, nested]) => [key, sanitizeWireValue(nested)] as const)
        .filter(([, nested]) => !isEmptyValue(nested)),
    );
    return Object.keys(next).length > 0 ? next : undefined;
  }
  return value;
}

export interface PendingFileUpload {
  blockId: string;
  fieldId?: string;
  file: File;
  fieldKey: string;
}

export function collectPendingFileUploads(
  form: ResolvedForm,
  values: PatientFormValues,
): PendingFileUpload[] {
  const out: PendingFileUpload[] = [];
  for (const block of form.blocks) {
    if (!block.is_visible) continue;
    collectBlockFileUploads(block, values[block.id], out);
  }
  return out;
}

function collectBlockFileUploads(
  block: ResolvedFormBlock,
  raw: unknown,
  out: PendingFileUpload[],
): void {
  if (mapFieldControlType(block.block_type) === 'file' && isPendingFileValue(raw)) {
    out.push({ blockId: block.id, file: raw, fieldKey: block.id });
    return;
  }

  if (!isRecord(raw) || isPendingFileValue(raw)) return;

  for (const field of block.catalog?.fields ?? []) {
    if (mapFieldControlType(field.type ?? 'text') !== 'file') continue;
    const nested = raw[field.id];
    if (!isPendingFileValue(nested)) continue;
    out.push({
      blockId: block.id,
      fieldId: field.id,
      file: nested,
      fieldKey: `${block.id}.${field.id}`,
    });
  }
}

export function applyUploadedFileKey(
  values: PatientFormValues,
  upload: PendingFileUpload,
  fileKey: string,
): PatientFormValues {
  const next = { ...values };
  if (upload.fieldId) {
    const block =
      isRecord(next[upload.blockId]) && !isPendingFileValue(next[upload.blockId])
        ? { ...(next[upload.blockId] as Record<string, unknown>) }
        : {};
    block[upload.fieldId] = fileKey;
    next[upload.blockId] = block as PatientFormBlockPayloadValue;
    return next;
  }
  next[upload.blockId] = fileKey;
  return next;
}

function stripPendingSignatureData(
  form: ResolvedForm,
  blockId: string,
  values: PatientFormValues,
  next: unknown,
): unknown {
  const block = form.blocks.find((b) => b.id === blockId);
  if (!block) return next;

  if (mapFieldControlType(block.block_type) === 'signature') {
    const raw = values[blockId];
    if (typeof raw === 'string' && raw.startsWith('data:')) return undefined;
    return next;
  }

  if (!isRecord(next)) return next;

  let result: Record<string, unknown> = { ...next };
  for (const field of block.catalog?.fields ?? []) {
    if (mapFieldControlType(field.type ?? 'text') !== 'signature') continue;
    const sigVal = isRecord(values[blockId])
      ? (values[blockId] as Record<string, unknown>)[field.id]
      : undefined;
    if (typeof sigVal === 'string' && sigVal.startsWith('data:')) {
      result = Object.fromEntries(Object.entries(result).filter(([fieldId]) => fieldId !== field.id));
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

/** Build PATCH/autosave payload from visible blocks (pending signature data URIs stripped). */
export function buildWirePayload(
  form: ResolvedForm,
  values: PatientFormValues,
  signatures?: SignatureLocation[],
): PatientFormSubmissionPayload {
  const visibleIds = new Set(form.blocks.filter((b) => b.is_visible).map((b) => b.id));
  const out: PatientFormSubmissionPayload = {};
  for (const [key, value] of Object.entries(values)) {
    if (!visibleIds.has(key)) continue;

    let next = sanitizeWireValue(value);

    if (signatures) {
      const sigsOnBlock = signatures.filter((s) => s.blockId === key);
      let skipBlock = false;
      for (const sig of sigsOnBlock) {
        if (!sig.fieldId) {
          const raw = values[key];
          if (typeof raw === 'string' && raw.startsWith('data:')) skipBlock = true;
        } else if (isRecord(next)) {
          next = Object.fromEntries(
            Object.entries(next).filter(([fieldId]) => fieldId !== sig.fieldId),
          );
        }
      }
      if (skipBlock) continue;
    } else {
      next = stripPendingSignatureData(form, key, values, next);
    }

    if (isEmptyValue(next)) continue;
    if (typeof next === 'string' && (next.startsWith('http://') || next.startsWith('https://'))) continue;
    out[key] = next as PatientFormBlockPayloadValue;
  }
  return out;
}
